import {
    createSelector,
} from '@reduxjs/toolkit';

import {
    selectParlay,
    selectUserParlay,
    selectSinglesTotalStake,
} from './parlaySelectors';

import {
    selectParlayTabTotalStake,
    selectSinglesTabTotalStake,
} from '~Betslip/selectors/totalStakeSelectors';

import {
    selectMultiplesTotalStake,
    selectIsMultiCastMarket,
} from '~Betslip/selectors/userMultiplesSelectors';

import {
    selectUserSelections,
    selectAnySelectionReverseCast,
} from './userSelectionsSelectors';

import {
    selectAllFreeBets,
} from './freeBetSelectors';

import {
    selectUserMultiples,
} from './userMultiplesSelectors';

import {
    selectAvailableBetTypes,
} from './availableBetTypesSelectors';

import {
    selectAnySelectionSuspended,
} from './selectionDetailsSelectors';

import {
    selectShowContingenciesNotification,
} from '~Betslip/selectors/contingenciesSelectors';

import {
    selectDisplaySinglesContent,
    selectDisplayMultiplesContent,
    selectDisplayFullslip,
    selectDisplayParlayContent,
} from './uiSelectors';

import {
    selectCountUserSelections,
} from '~Betslip/selectors/userSelectionsSelectors';

import {
    calculateSingleSelectionTotalStake,
    calculateMultipleTotalStake,
} from '~Betslip/utils/stakeCalculators';

import {
    getDepositDetails,
} from '~Betslip/utils/depositChecks';

import {
    selectUserBalance,
} from '~SpectateStore/selectors/userSelectors';

// This does not include the freebet stake.
export const selectHasTotalSinglesExceedBalance = createSelector(
    selectUserSelections,
    selectAllFreeBets,
    selectUserBalance,
    (userSelections, freebets, userBalance) => {
        const singlesTotalStake = userSelections.reduce(
            (acc, current) => {
                let freebetStake = null;
                if (freebets[current.id]) {
                    const selectedFreebet = freebets[current.id].find(currentFreebet => currentFreebet.id === current.free_bet_id);
                    freebetStake = selectedFreebet?.value || null;
                }

                return acc + calculateSingleSelectionTotalStake(current, freebetStake, true);
            }, 0
        );

        const freeBetApplied = Boolean(userSelections.find(({ free_bet_id }) => free_bet_id !== null));

        return getDepositDetails(userBalance, singlesTotalStake, freeBetApplied);
    }
);

export const selectHasParlayExceedBalance = createSelector(
    [
        selectParlay,
        selectUserParlay,
        selectAllFreeBets,
        selectSinglesTotalStake,
        selectAnySelectionSuspended,
        selectUserBalance,
    ],
    (parlay, userParlay, freebets, singlesTotalStake, selectionsSuspended, userBalance) => {
        if (!parlay) {
            return getDepositDetails(userBalance, singlesTotalStake, false);
        }

        const {
            stake,
            each_way_selected,
            free_bet_id,
        } = userParlay;

        const betslipId = parlay.id;
        let freebetStake = null;
        let selectedFreebet = false;

        if (freebets[betslipId]) {
            selectedFreebet = freebets[betslipId].find(fb => fb.id === free_bet_id);
            freebetStake = selectedFreebet?.value || null;
        }

        const parlayTotalStake = calculateMultipleTotalStake(stake, each_way_selected, undefined, freebetStake, true);

        let totalStake = singlesTotalStake;
        if (!selectionsSuspended) {
            totalStake += parlayTotalStake;
        }

        return getDepositDetails(userBalance, totalStake, Boolean(selectedFreebet));

    }
);

export const selectHasMultiplesExceedBalance = createSelector(
    [
        selectUserMultiples,
        selectAvailableBetTypes,
        selectAllFreeBets,
        selectUserBalance,
    ],
    (userMultiples, availableBetTypes, freebets, userBalance) => {
        if (!availableBetTypes.length) {
            return {};
        }

        const multiplesTotalStake = userMultiples.reduce(
            (acc, curr) => {
                const {
                    id: currId,
                    stake,
                    each_way_selected,
                    free_bet_id,
                } = curr;
                const availableBetType = availableBetTypes.find(({ id }) => id === currId);
                // if no match we default to 0
                const betTypeTotal = availableBetType?.total || 0;

                let freebetStake = null;
                if(freebets[currId]) {
                    const selectedFreebet = freebets[currId].find(currentFreebet => currentFreebet.id === free_bet_id);
                    freebetStake = selectedFreebet?.value || null;
                }

                return acc + calculateMultipleTotalStake(stake, each_way_selected, betTypeTotal, freebetStake, true);
            },
            0
        );

        const freeBetApplied = Boolean(userMultiples.find(({ free_bet_id }) => free_bet_id !== null));

        return getDepositDetails(userBalance, multiplesTotalStake, freeBetApplied);
    }
);

export const selectShowDepositNotification = createSelector(
    [
        selectHasTotalSinglesExceedBalance,
        selectHasParlayExceedBalance,
        selectHasMultiplesExceedBalance,
        selectDisplaySinglesContent,
        selectDisplayMultiplesContent,
    ],
    (singles, parlay, multiples, displaySingle, displayMultiples) => {

        if (displaySingle) {
            return singles.isDepositNeeded;
        } else if (displayMultiples) {
            return multiples.isDepositNeeded;
        }

        return parlay.isDepositNeeded;
    }
);

export const selectShouldShowReturns = createSelector(
    [
        selectDisplaySinglesContent,
        selectDisplayParlayContent,
        selectDisplayMultiplesContent,
        selectSinglesTabTotalStake,
        selectParlayTabTotalStake,
        selectMultiplesTotalStake,
        selectDisplayFullslip,
        selectCountUserSelections,
    ],
    (displaySingle, displayParlay, displayMultiples, singleStake, parlayStake, multipleStake, displayFullslip, userSelectionsCounter) => {
        return (userSelectionsCounter && displayFullslip)
        || (((displaySingle && singleStake !== 0)
        || (displayParlay && parlayStake !== 0)
        || (displayMultiples && multipleStake !== 0))
        && !displayFullslip);
    }
);

export const selectShouldShowOpenBetslip = createSelector(
    [
        selectShowContingenciesNotification,
        selectAnySelectionReverseCast,
        selectDisplayFullslip,
        selectIsMultiCastMarket,
    ],
    (showContingenciesNotification, isCastMarketAny, displayFullslip, isMultiCastMarket) => {
        return (!displayFullslip)
        && (showContingenciesNotification
        || isCastMarketAny
        || isMultiCastMarket);
    }
);
